import React from "react";
import { useSearchParams } from "react-router-dom";
import { Check, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import AuditForm from "@/components/AuditForm";
import { BOOKING_URL, WHATSAPP_URL, CONTACT_EMAIL } from "@/config/site";

const COVERS = [
  "How leads come in and how fast you reply",
  "How you reach new customers",
  "How leads are followed up",
  "Your tools, and what is still done by hand",
];

export default function AuditPage() {
  const [searchParams] = useSearchParams();
  const isCustom = searchParams.get("enquiry") === "custom";

  const label = isCustom ? "Custom workflows" : "Free audit";
  const heading = isCustom ? "Book a discovery call" : "Free Sales Workflow Audit";
  const subline = isCustom
    ? "A 45 minute call on your goals, workflows, markets, tools and team. We send a proposal with the workflows, timeline and price."
    : "A 30 minute call covering four areas, then a one-page summary within 48 hours.";

  return (
    <main data-testid="audit-page">
      <Seo
        title={isCustom ? "Book a discovery call" : "Free Sales Workflow Audit"}
        description={
          isCustom
            ? "Book a 45 minute discovery call with Raki Solutions in Singapore. We map your goals, workflows, markets and tools, then send a proposal with timeline and price."
            : "Book a free 30 minute sales workflow audit in Singapore. We review how you get and handle leads, then send a one-page summary within 48 hours."
        }
        path="/audit"
        image={isCustom ? undefined : "/og-audit-2026.jpg"}
        imageAlt={
          isCustom
            ? "Raki Solutions. Your sales process, automated."
            : "Free Sales Workflow Audit. A 30 minute call, then a one-page summary within 48 hours."
        }
      />

      <section className="px-6 sm:px-8 pt-16 sm:pt-20 pb-14">
        <div className="max-w-3xl mx-auto">
          <p className="font-semibold text-xs uppercase tracking-[0.22em] text-[#C8703A]">{label}</p>
          <h1 className="mt-6 font-fraunces font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1F1F1C]" data-testid="audit-headline">
            {heading}
          </h1>
          <p className="mt-7 text-base md:text-lg text-[#4A463F] leading-relaxed max-w-2xl" data-testid="audit-subline">
            {subline}
          </p>

          {!isCustom && (
            <>
              <ul className="mt-10 grid sm:grid-cols-2 gap-3 max-w-3xl">
                {COVERS.map((c, i) => (
                  <li key={c} className="flex gap-3 items-start" data-testid={`audit-cover-${i + 1}`}>
                    <Check className="w-5 h-5 text-[#C8703A] shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-sm text-[#1F1F1C] leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-10 text-sm md:text-base text-[#4A463F] leading-relaxed max-w-2xl" data-testid="audit-what-you-get">
                <span className="font-semibold text-[#1F1F1C]">What you get:</span> what is working, the 3 biggest gaps, and what we recommend.
              </p>
            </>
          )}
        </div>
      </section>

      <section className="px-6 sm:px-8 pb-20 sm:pb-28">
        <div className="max-w-3xl mx-auto">
          <AuditForm />

          <div className="mt-14" data-testid="audit-calendar">
            <h2 className="font-fraunces font-extrabold text-2xl sm:text-3xl tracking-tight text-[#1F1F1C]">
              Pick a time
            </h2>
            <p className="mt-3 text-sm text-[#4A463F] leading-relaxed">
              Choose a slot that suits you and you will get a calendar invite straight away. The
              booking page opens in a new tab.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-press mt-7 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#C8703A] text-[#F4E9D8] px-7 py-4 font-semibold text-sm sm:text-base hover:bg-[#B35F2C]"
              data-testid="audit-calendar-link"
            >
              Open the booking calendar <ExternalLink className="w-4 h-4" strokeWidth={2.25} />
            </a>
            <p className="mt-5 text-sm text-[#4A463F]">
              Prefer to message first? Reach us on{" "}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[#C8703A] hover:underline underline-offset-4"
                data-testid="audit-whatsapp-link"
              >
                WhatsApp
              </a>{" "}
              or email{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-semibold text-[#C8703A] hover:underline underline-offset-4"
                data-testid="audit-email-link"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
